/**
 * AdminReferralHistory
 * 관리자 레퍼럴 발생이력 페이지
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI, type ReferralHistoryItem } from '../../services/api';
import { tokens } from '../../design-system/tokens';
import { Button } from '../../components/atoms/Button/Button';

export const AdminReferralHistory: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [credits, setCredits] = useState<ReferralHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 50;

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [referrerUid, setReferrerUid] = useState('');

  // 관리자 권한 확인
  useEffect(() => {
    if (userInfo && userInfo.memberGrade !== 'admin') {
      navigate('/');
    }
  }, [userInfo, navigate]);

  // 레퍼럴 이력 로드 (userInfo가 로드되고 admin인 경우에만)
  useEffect(() => {
    if (userInfo && userInfo.memberGrade === 'admin') {
      loadReferralHistory();
    }
  }, [userInfo, currentPage, startDate, endDate, referrerUid]);

  const loadReferralHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: currentPage,
        limit
      };

      if (startDate) params.startDate = new Date(startDate).toISOString();
      if (endDate) {
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        params.endDate = endDateTime.toISOString();
      }
      if (referrerUid) params.referrerUid = referrerUid;

      const response = await adminAPI.getReferralHistory(params);

      if (response.success) {
        setCredits(response.data.credits);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
        setCurrentPage(response.data.currentPage);
      }
    } catch (err: any) {
      console.error('Failed to load referral history:', err);
      setError('레퍼럴 발생이력을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (credits.length === 0) {
      alert('내보낼 데이터가 없습니다.');
      return;
    }

    // CSV 헤더
    const headers = [
      '발생일시',
      '추천인 이름',
      '추천인 이메일',
      '추천인 등급',
      '피추천인 이름',
      '피추천인 이메일',
      '피추천인 등급',
      '결제 상품',
      '결제 금액',
      '적립 포인트',
      '정산율(%)',
      '상태'
    ];

    // CSV 데이터
    const rows = credits.map(credit => [
      new Date(credit.createdAt).toLocaleString('ko-KR'),
      credit.referrer.displayName,
      credit.referrer.email,
      getMemberGradeLabel(credit.referrer.memberGrade),
      credit.referred.displayName,
      credit.referred.email,
      getMemberGradeLabel(credit.referred.memberGrade),
      credit.payment.productName,
      credit.payment.amount.toLocaleString(),
      credit.creditAmount.toLocaleString(),
      (credit.percentage * 100).toFixed(0),
      credit.status === 'earned' ? '적립' : credit.status
    ]);

    // CSV 생성
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // BOM 추가 (한글 엑셀 호환)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `referral_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getMemberGradeLabel = (grade: string): string => {
    switch (grade) {
      case 'admin': return '관리자';
      case 'special': return '특별회원';
      case 'regular': return '정회원';
      case 'member': return '일반회원';
      default: return grade;
    }
  };

  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setReferrerUid('');
    setCurrentPage(1);
  };

  return (
    <Content>
      <Header>
        <BackButton onClick={() => navigate('/admin')}>← 뒤로</BackButton>
        <Title>레퍼럴 발생이력</Title>
        <Spacer />
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* 필터 섹션 */}
      <FilterSection>
        <FilterRow>
          <FilterGroup>
            <FilterLabel>시작일</FilterLabel>
            <DateInput
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>종료일</FilterLabel>
            <DateInput
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>추천인 UID (선택)</FilterLabel>
            <TextInput
              type="text"
              placeholder="UID 입력"
              value={referrerUid}
              onChange={(e) => {
                setReferrerUid(e.target.value);
                setCurrentPage(1);
              }}
            />
          </FilterGroup>
        </FilterRow>

        <FilterActions>
          <Button
            variant="secondary"
            size="small"
            onClick={handleResetFilters}
          >
            필터 초기화
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleExportCSV}
            disabled={credits.length === 0}
          >
            CSV 내보내기
          </Button>
        </FilterActions>
      </FilterSection>

      {/* 통계 요약 */}
      <SummarySection>
        <SummaryItem>
          <SummaryLabel>전체 레퍼럴 수</SummaryLabel>
          <SummaryValue>{totalCount.toLocaleString()}건</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>현재 페이지</SummaryLabel>
          <SummaryValue>{credits.length.toLocaleString()}건</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>총 적립 포인트</SummaryLabel>
          <SummaryValue>
            {credits.reduce((sum, c) => sum + c.creditAmount, 0).toLocaleString()}원
          </SummaryValue>
        </SummaryItem>
      </SummarySection>

      {loading ? (
        <LoadingMessage>데이터를 불러오는 중...</LoadingMessage>
      ) : credits.length === 0 ? (
        <EmptyMessage>레퍼럴 발생이력이 없습니다.</EmptyMessage>
      ) : (
        <>
          <TableWrapper>
            <HistoryTable>
              <thead>
                <tr>
                  <th>발생일시</th>
                  <th>추천인</th>
                  <th>피추천인</th>
                  <th>결제 정보</th>
                  <th>적립 포인트</th>
                  <th>정산율</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {credits.map((credit) => (
                  <tr key={credit.creditId}>
                    <td>
                      <DateText>
                        {new Date(credit.createdAt).toLocaleString('ko-KR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </DateText>
                    </td>
                    <td>
                      <UserInfo>
                        <UserName>{credit.referrer.displayName}</UserName>
                        <UserEmail>{credit.referrer.email}</UserEmail>
                        <UserGrade>{getMemberGradeLabel(credit.referrer.memberGrade)}</UserGrade>
                      </UserInfo>
                    </td>
                    <td>
                      <UserInfo>
                        <UserName>{credit.referred.displayName}</UserName>
                        <UserEmail>{credit.referred.email}</UserEmail>
                        <UserGrade>{getMemberGradeLabel(credit.referred.memberGrade)}</UserGrade>
                      </UserInfo>
                    </td>
                    <td>
                      <PaymentInfo>
                        <ProductName>{credit.payment.productName}</ProductName>
                        <PaymentAmount>{credit.payment.amount.toLocaleString()}원</PaymentAmount>
                      </PaymentInfo>
                    </td>
                    <td>
                      <CreditAmount>{credit.creditAmount.toLocaleString()}원</CreditAmount>
                    </td>
                    <td>
                      <PercentageText>{(credit.percentage * 100).toFixed(0)}%</PercentageText>
                    </td>
                    <td>
                      <StatusBadge $status={credit.status}>
                        {credit.status === 'earned' ? '적립' : credit.status}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          </TableWrapper>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <PaginationSection>
              <PaginationButton
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                이전
              </PaginationButton>

              <PageInfo>
                {currentPage} / {totalPages} 페이지
              </PageInfo>

              <PaginationButton
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                다음
              </PaginationButton>
            </PaginationSection>
          )}
        </>
      )}
    </Content>
  );
};

// Styled Components
const Content = styled.div`
  padding: ${tokens.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${tokens.spacing[6]};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${tokens.colors.primary[500]};
  font-size: ${tokens.typography.fontSize.base};
  cursor: pointer;
  padding: ${tokens.spacing[2]};

  &:hover {
    color: ${tokens.colors.primary[600]};
  }
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['2xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  margin: 0;
`;

const Spacer = styled.div`
  width: 60px;
`;

const ErrorMessage = styled.div`
  background: ${tokens.colors.semantic.error}15;
  color: ${tokens.colors.semantic.error};
  padding: ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.md};
  margin-bottom: ${tokens.spacing[6]};
  text-align: center;
`;

const FilterSection = styled.div`
  background: ${tokens.colors.neutral[50]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.borderRadius.md};
  padding: ${tokens.spacing[4]};
  margin-bottom: ${tokens.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[4]};
`;

const FilterRow = styled.div`
  display: flex;
  gap: ${tokens.spacing[4]};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[2]};
  flex: 1;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
`;

const DateInput = styled.input`
  padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
  border: 1px solid ${tokens.colors.neutral[300]};
  border-radius: ${tokens.borderRadius.base};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[800]};

  &:focus {
    outline: none;
    border-color: ${tokens.colors.primary[500]};
  }
`;

const TextInput = styled.input`
  padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
  border: 1px solid ${tokens.colors.neutral[300]};
  border-radius: ${tokens.borderRadius.base};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[800]};

  &:focus {
    outline: none;
    border-color: ${tokens.colors.primary[500]};
  }

  &::placeholder {
    color: ${tokens.colors.neutral[400]};
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};
  justify-content: flex-end;
`;

const SummarySection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${tokens.spacing[4]};
  margin-bottom: ${tokens.spacing[6]};
`;

const SummaryItem = styled.div`
  background: ${tokens.colors.neutral[0]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.borderRadius.md};
  padding: ${tokens.spacing[4]};
  text-align: center;
`;

const SummaryLabel = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
  margin-bottom: ${tokens.spacing[2]};
`;

const SummaryValue = styled.div`
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.primary[500]};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  color: ${tokens.colors.neutral[500]};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  color: ${tokens.colors.neutral[500]};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-bottom: ${tokens.spacing[6]};
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${tokens.typography.fontSize.sm};
  min-width: 900px;

  th {
    background: ${tokens.colors.neutral[100]};
    padding: ${tokens.spacing[3]};
    text-align: left;
    font-weight: ${tokens.typography.fontWeight.semibold};
    color: ${tokens.colors.neutral[700]};
    border-bottom: 2px solid ${tokens.colors.neutral[300]};
    white-space: nowrap;
  }

  td {
    padding: ${tokens.spacing[3]};
    border-bottom: 1px solid ${tokens.colors.neutral[200]};
    vertical-align: top;
  }

  tr:hover td {
    background: ${tokens.colors.neutral[50]};
  }
`;

const DateText = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[700]};
  white-space: nowrap;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[1]};
`;

const UserName = styled.div`
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[800]};
  font-size: ${tokens.typography.fontSize.sm};
`;

const UserEmail = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[600]};
`;

const UserGrade = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[500]};
  font-style: italic;
`;

const PaymentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[1]};
`;

const ProductName = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[800]};
`;

const PaymentAmount = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[600]};
`;

const CreditAmount = styled.div`
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.semantic.success};
  font-size: ${tokens.typography.fontSize.sm};
  white-space: nowrap;
`;

const PercentageText = styled.div`
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: ${tokens.spacing[1]} ${tokens.spacing[3]};
  border-radius: ${tokens.borderRadius.full};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.medium};
  background: ${props => {
    switch (props.$status) {
      case 'earned': return tokens.colors.semantic.success + '15';
      default: return tokens.colors.neutral[100];
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'earned': return tokens.colors.semantic.success;
      default: return tokens.colors.neutral[700];
    }
  }};
`;

const PaginationSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${tokens.spacing[4]};
  padding: ${tokens.spacing[4]};
`;

const PaginationButton = styled.button`
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  background: ${tokens.colors.neutral[0]};
  border: 1px solid ${tokens.colors.neutral[300]};
  border-radius: ${tokens.borderRadius.base};
  color: ${tokens.colors.neutral[700]};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};

  &:hover:not(:disabled) {
    border-color: ${tokens.colors.primary[500]};
    color: ${tokens.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[700]};
  font-weight: ${tokens.typography.fontWeight.medium};
`;
